import React, { useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Routes, Route, Navigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";

import "./App.css";
import { roles } from "@src/shared/roles";
import { routes } from "@src/shared/routes";
import { withLayout } from "@src/layout/Layout";
import { hasPermission } from "./utils/hasPermission";
import { initalApp } from "./store/auth/actionCreators";
import { selectAuthLoader } from "./store/auth/selector";
import { Snackbar } from "@src/components/Snackbar/Snackbar";
import { selectFullRefreshLoader } from "./store/app_manager/selector";
import { AnalyticsPage } from "./pages/Analytics/AnalyticsPage";
import { LoginPage } from "./pages/AuthPage/LoginPage";
import { setCookie } from "./utils/setCookie";

const MenuPage = React.lazy(
  () => import("@src/pages/MenuPage/MenuPage.Container")
);
const OrderPage = React.lazy(
  () => import("@src/pages/OrderPage/OrderPage.Container")
);
const PromotionPage = React.lazy(
  () => import("@src/pages/Promotions/view/PromotionsPage")
);
const CallCenterPage = React.lazy(
  () => import("@src/pages/CallCenter/CallCenter.Container")
);
const StopListPage = React.lazy(
  () => import("@src/pages/StopListPage/StopListPage.Container")
);
const LimitPage = React.lazy(
  () => import("@src/pages/LimitPage/LimitPage")
);
const LocationPage = React.lazy(
  () => import("@src/pages/LocationPage/LocationPage.Container")
);
const TimeSettingPage = React.lazy(
  () => import("@src/pages/SettingPage/RestaurantSettingPage")
);
const PosSettingsPage = React.lazy(
  () => import("@src/pages/POSSettings/PosSettings.Container")
);
const UploadMenuPage = React.lazy(
  () => import("@src/pages/UploadMenuPage/UploadMenuPage.Container")
);
const CreateMenuSuccess = React.lazy(
  () => import("@src/pages/CreateMenuSuccessPage/CreateMenuSuccessPage")
);
const ConfigureMenuPage = React.lazy(
  () => import("@src/pages/ConfigureMenuPage/ConfigureMenuPage.Container")
);
const AttributeGroupPage = React.lazy(
  () => import("@src/pages/AttributeGroupPage/AttributeGroupPage.Container")
);
const PublicationMenuPage = React.lazy(
  () => import("@src/pages/PublicationMenuPage/PublicationMenuPage.Container")
);

const AccessPage = React.lazy(
  () => import("@src/pages/AccessPage/view/AccessPage.Container")
);

const CronPage = React.lazy(() => import("@src/pages/CronPage/CronPage"));

const App: React.FC = () => {
  const dispatch = useDispatch();
  const authLoader = useSelector(selectAuthLoader);
  const fullRefreshLoader = useSelector(selectFullRefreshLoader);

  useLayoutEffect(() => {
    dispatch(initalApp());
    setCookie();
  }, []);

  if (fullRefreshLoader) {
    return <CircularProgress />;
  }

  return (
    <React.Fragment>
      {authLoader ? (
        <CircularProgress />
      ) : (
        <Routes>
          {hasPermission(roles.order) && (
            <Route
              path={routes.order}
              element={
                <React.Suspense fallback={<CircularProgress />}>
                  <OrderPage />
                </React.Suspense>
              }
            />
          )}
          {hasPermission(roles.order) && (
            <Route
              path={routes.activeOrders}
              element={
                <React.Suspense fallback={<CircularProgress />}>
                  <OrderPage />
                </React.Suspense>
              }
            />
          )}
          {hasPermission(roles.stopList) && (
            <Route
              path={`${routes.stopList}/*`}
              element={
                <React.Suspense fallback={<CircularProgress />}>
                  <StopListPage />
                </React.Suspense>
              }
            />
          )}
          {hasPermission(roles.stopListBalance) && (
            <Route
              path={`${routes.stopListBalance}/*`}
              element={
                <React.Suspense fallback={<CircularProgress />}>
                  <LimitPage />
                </React.Suspense>
              }
            />
          )}
          {hasPermission(roles.cron) && (
            <Route
              path={`${routes.cron}/*`}
              element={
                <React.Suspense fallback={<CircularProgress />}>
                  <CronPage />
                </React.Suspense>
              }
            />
          )}
          {hasPermission(roles.analytics, routes.analytics) && (
            <Route
              path={`${routes.analytics}/*`}
              element={
                <React.Suspense fallback={<CircularProgress />}>
                  <AnalyticsPage />
                </React.Suspense>
              }
            />
          )}
          {hasPermission(roles.analytics, routes.analytics) && (
            <Route path={`${routes.analytics}/*`} element={<AnalyticsPage />} />
          )}
          {hasPermission(roles.menu, routes.menu) && (
            <Route
              path={routes.menu}
              element={
                <React.Suspense fallback={<CircularProgress />}>
                  <MenuPage />
                </React.Suspense>
              }
            />
          )}
          {hasPermission(roles.promo) && (
            <Route
              path={routes.promo}
              element={
                <React.Suspense fallback={<CircularProgress />}>
                  <PromotionPage />
                </React.Suspense>
              }
            />
          )}
          {hasPermission(roles.configureMenu, routes.configureMenu) && (
            <Route
              path={`${routes.configureMenu}/*`}
              element={
                <React.Suspense fallback={<CircularProgress />}>
                  <ConfigureMenuPage />
                </React.Suspense>
              }
            />
          )}
          {hasPermission(roles.location) && (
            <Route
              path={`${routes.location}/*`}
              element={
                <React.Suspense fallback={<CircularProgress />}>
                  <LocationPage />
                </React.Suspense>
              }
            />
          )}
          {hasPermission(roles.uploadMenu, routes.uploadMenu) && (
            <Route
              path={routes.uploadMenu}
              element={
                <React.Suspense fallback={<CircularProgress />}>
                  <UploadMenuPage />
                </React.Suspense>
              }
            />
          )}
          {hasPermission(roles.createMenuSuccess, routes.createMenuSuccess) && (
            <Route
              path={routes.createMenuSuccess}
              element={
                <React.Suspense fallback={<CircularProgress />}>
                  <CreateMenuSuccess />
                </React.Suspense>
              }
            />
          )}
          {hasPermission(roles.publicationMenu, routes.publicationMenu) && (
            <Route
              path={routes.publicationMenu}
              element={
                <React.Suspense fallback={<CircularProgress />}>
                  <PublicationMenuPage />
                </React.Suspense>
              }
            />
          )}
          {hasPermission(roles.timeSetting) && (
            <Route
              path={routes.timeSetting}
              element={
                <React.Suspense fallback={<CircularProgress />}>
                  <TimeSettingPage />
                </React.Suspense>
              }
            />
          )}
          {hasPermission(roles.callCenter) && (
            <Route
              path={routes.callCenter}
              element={
                <React.Suspense fallback={<CircularProgress />}>
                  <CallCenterPage />
                </React.Suspense>
              }
            />
          )}

          {hasPermission(roles.attributeGroup) && (
            <Route
              path={`${routes.attributeGroup}/*`}
              element={
                <React.Suspense fallback={<CircularProgress />}>
                  <AttributeGroupPage />
                </React.Suspense>
              }
            />
          )}
          {hasPermission(roles.posSettings) && (
            <Route
              path={`${routes.posSettings}`}
              element={
                <React.Suspense fallback={<CircularProgress />}>
                  <PosSettingsPage />
                </React.Suspense>
              }
            />
          )}
          {hasPermission(roles.access) && (
            <Route
              path={routes.access}
              element={
                <React.Suspense fallback={<CircularProgress />}>
                  <AccessPage />
                </React.Suspense>
              }
            />
          )}
          {hasPermission(roles.order) && (
            <Route path="*" element={<Navigate to={routes.order} />} />
          )}
          {hasPermission(roles.callCenter) && (
            <Route path="*" element={<Navigate to={routes.callCenter} />} />
          )}
        </Routes>
      )}
      <Snackbar />
    </React.Fragment>
  );
};

const AppWithLayout = withLayout(App) as any;

function decodeJwt(token: string): Record<string, any> {
  try {
    const part = token.split(".")[1];
    // Add base64url padding before atob
    const padded = part + "==".slice(0, (4 - (part.length % 4)) % 4);
    return JSON.parse(atob(padded.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return {};
  }
}

function extractRoles(payload: Record<string, any>): string[] {
  const roles =
    payload["cognito:groups"] ||
    payload["groups"] ||
    payload["roles"] ||
    payload["role"] ||
    [];
  return Array.isArray(roles) ? roles : [roles].filter(Boolean);
}

function getValidToken(): { token: string; roles: string[] } | null {
  const token = localStorage.getItem("token");
  if (!token) return null;
  const payload = decodeJwt(token);
  const roles: string[] = extractRoles(payload);
  if (roles.length === 0) {
    // Stale or wrong token — clear it
    localStorage.removeItem("token");
    localStorage.removeItem("userRole");
    localStorage.removeItem("username");
    return null;
  }
  return { token, roles };
}

const Root: React.FC = () => {
  const [authed, setAuthed] = useState(() => {
    const valid = getValidToken();
    if (valid) {
      localStorage.setItem("userRole", JSON.stringify(valid.roles));
      return true;
    }
    return false;
  });

  if (!authed) {
    return (
      <LoginPage
        onSuccess={(token, roles) => {
          localStorage.setItem("token", token);
          localStorage.setItem("userRole", JSON.stringify(roles));
          setAuthed(true);
        }}
      />
    );
  }

  return <AppWithLayout />;
};

export default Root;
